"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import {
  CopyButton,
  DebugCodePath,
  DebugSection,
} from "../components/debug-primitives";
import {
  useRenderedComponents,
  type RenderedComponent,
} from "../hooks/use-rendered-components";
import { ComponentHighlightOverlay } from "../overlays/component-highlight-overlay";
import { ComponentLabelsOverlay } from "../overlays/component-labels-overlay";
import { useFileLines } from "../utils/file-lines";

export function RenderedComponentsSection({
  pathname,
  filter,
  forceOpen,
}: {
  pathname: string;
  filter?: string;
  forceOpen?: boolean;
}) {
  const t = useTranslations("debug.renderedComponents");
  const { components, scanning, scan } = useRenderedComponents(pathname);
  const filtered = filter
    ? components.filter((c) =>
        c.path.toLowerCase().includes(filter.toLowerCase()),
      )
    : components;

  const [hovered, setHovered] = useState<RenderedComponent | null>(null);
  const [labelsEnabled, setLabelsEnabled] = useState(false);

  const componentPaths = useMemo(
    () => components.map((c) => c.path),
    [components],
  );
  useFileLines(componentPaths);

  if (filter && filtered.length === 0 && !scanning) return null;

  return (
    <>
      <DebugSection
        title={
          components.length === 0
            ? t("title")
            : filter
              ? t("titleFiltered", {
                  visible: filtered.length,
                  total: components.length,
                })
              : t("titleCount", { count: components.length })
        }
        accent="border-sky-500"
        defaultOpen={components.length > 0}
        {...(forceOpen !== undefined ? { forceOpen } : {})}
        actions={
          <div className="flex items-center gap-1">
            <button
              onClick={() => setLabelsEnabled((prev) => !prev)}
              className={cn(
                "text-sm px-2 py-0.5 rounded border transition-colors",
                labelsEnabled
                  ? "border-sky-500/60 bg-sky-500/10 text-sky-600 dark:text-sky-400"
                  : "border-border bg-muted/50 text-muted-foreground hover:text-foreground",
              )}
              title={t("labelsTitle")}
            >
              {labelsEnabled ? t("hideLabels") : t("labels")}
            </button>
            <button
              onClick={scan}
              disabled={scanning}
              className="text-sm px-2 py-0.5 rounded border border-border bg-muted/50 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
            >
              {scanning ? t("scanning") : t("refresh")}
            </button>
          </div>
        }
      >
        {scanning ? (
          <div className="text-sm text-muted-foreground py-1">
            {t("scanningTree")}
          </div>
        ) : components.length === 0 ? (
          <div className="text-sm text-muted-foreground py-1">{t("empty")}</div>
        ) : (
          <div className="space-y-0.5">
            {filtered.map((c) => (
              <div
                key={c.path}
                className="flex items-center gap-2 group cursor-default"
                onMouseEnter={() => setHovered(c)}
                onMouseLeave={() => setHovered(null)}
              >
                <span className="text-muted-foreground/50 shrink-0">{"→"}</span>
                <DebugCodePath
                  path={c.path}
                  className={cn(
                    "flex-1 transition-colors",
                    hovered?.path === c.path
                      ? "text-sky-500"
                      : "text-foreground",
                  )}
                />
                <CopyButton text={c.path} />
              </div>
            ))}
          </div>
        )}
      </DebugSection>

      {/* DOM overlays — portal-rendered, pointer-events: none */}
      {hovered && <ComponentHighlightOverlay component={hovered} />}
      {labelsEnabled && components.length > 0 && (
        <ComponentLabelsOverlay
          components={filtered.length > 0 ? filtered : components}
        />
      )}
    </>
  );
}
